import type { ImageSourcePropType } from "react-native";
import { useDevice } from "../../useDevice";
import Box from "../Box";
import EmptyState from "../EmptyView";
import type { MasteryListItemProps } from "../MasteryListItem";
import ScrollContainer from "../ScrollContainer";
import ProgressView from "./ProgressView";
import { ProgressViewSkeleton } from "./ProgressViewSkeleton";
import ProgressEmptySvg from "./progress-empty-state.svg";
import type {
	ExamCardData,
	LegacyTestsCardData,
	MasteryTagProgressData,
	SummaryCardData,
} from "./types";

const ProgressApplet = ({
	showPracticeTests,
	handleClickCard,
	showStudyPackViews,
	handleClickStudy,
	appFont,
	handleNavigation,
	handleLegacyReportDownload,
	downloadingLegacyReportId,
	progressNeedsImprovementCtaLabel,
	emptyState,
	masteryTagsProgressData,
	needsImprovementProgressData,
	progressPageSummaryCardData,
	practiceTestsCardsData,
	legacyTestsCardsData,
	showEmptyState,
	weakTagIds,
	handleClickInfo,
	renderNestedExamChips,
	needsImprovement,
	needsImprovementTagType,
	handleViewMorePracticeTests,
	handleViewMoreLegacyTests,
	isLoading,
}: {
	showPracticeTests?: boolean;
	handleClickCard?: (title: string) => void;
	showStudyPackViews?: boolean;
	handleClickStudy?: (tagIds: string[]) => void;
	appFont: string;
	handleNavigation: (numericId: number) => void;
	handleLegacyReportDownload?: (numericId: number) => void;
	downloadingLegacyReportId?: number;
	progressNeedsImprovementCtaLabel?: string;
	emptyState?: {
		title: string;
		subtitle: string;
		cta: string;
		onAction?: () => void;
	};
	masteryTagsProgressData: MasteryTagProgressData[];
	needsImprovementProgressData: MasteryTagProgressData | null;
	progressPageSummaryCardData?: SummaryCardData;
	practiceTestsCardsData?: ExamCardData;
	legacyTestsCardsData?: LegacyTestsCardData;
	showEmptyState?: boolean;
	handleClickInfo?: (text: string, title: string) => void;
	weakTagIds?: string[];
	renderNestedExamChips?: () => React.ReactNode;
	needsImprovement?: MasteryListItemProps[];
	needsImprovementTagType?: string;
	handleViewMorePracticeTests: () => void;
	handleViewMoreLegacyTests?: () => void;
	isLoading?: boolean;
}) => {
	const { isMobile } = useDevice();
	return (
		<ScrollContainer
			maxWidth="sm"
			// biome-ignore lint/a11y/useSemanticElements: <explanation>
			role="main"
			contentContainerStyle={{
				paddingTop: 24,
				paddingBottom: isMobile ? 16 : 24,
			}}
		>
			{renderNestedExamChips?.()}
			{isLoading ? (
				<Box paddingHorizontal={isMobile ? 16 : 0}>
					<ProgressViewSkeleton />
				</Box>
			) : showEmptyState && emptyState ? (
				<EmptyState
					title={emptyState.title}
					subtitle={emptyState.subtitle}
					cta={emptyState.cta}
					image={ProgressEmptySvg as ImageSourcePropType}
					handleAction={emptyState.onAction}
				/>
			) : (
				<Box paddingHorizontal={isMobile ? 16 : 0}>
					<ProgressView
						masteryTagsProgressData={masteryTagsProgressData}
						needsImprovementProgressData={needsImprovementProgressData}
						summaryCardData={progressPageSummaryCardData}
						practiceTestsCardData={practiceTestsCardsData}
						legacyTestsCardData={legacyTestsCardsData}
						showStudyPackViews={showStudyPackViews}
						showPracticeTests={showPracticeTests}
						handleClickCard={handleClickCard}
						handleClickStudy={
							handleClickStudy && weakTagIds?.length
								? () => handleClickStudy(weakTagIds)
								: undefined
						}
						appFont={appFont}
						handleNavigation={handleNavigation}
						handleLegacyReportDownload={handleLegacyReportDownload}
						downloadingLegacyReportId={downloadingLegacyReportId}
						progressNeedsImprovementCtaLabel={progressNeedsImprovementCtaLabel}
						handleClickInfo={handleClickInfo}
						needsImprovement={needsImprovement}
						needsImprovementTagType={needsImprovementTagType}
						onViewMorePracticeTests={handleViewMorePracticeTests}
						onViewMoreLegacyTests={handleViewMoreLegacyTests}
					/>
				</Box>
			)}
		</ScrollContainer>
	);
};

export default ProgressApplet;
